/**
 * The Objects tab inside a workspace.
 *
 * Same rows as the Objects page, reached from a rail rather than a table —
 * inside the workspace you are already in one graph, so the list of nodes is
 * navigation rather than a destination.
 */

import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {EmptyState, ErrorNotice, SkeletonRows} from '@/shared/components'
import { CategoryDot } from '../components'
import { ObjectRowsView } from './ObjectRowsView'
import { useObjectTables } from '../hooks'
import type { GraphModel } from '../types/brain.types'

export function ObjectsTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const tables = useObjectTables(graphId)
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('object') ?? undefined

  const selected = tables.data?.find((t) => t.node_type_id === selectedId) ?? tables.data?.[0]

  // Keep the selection in the URL, so a table is a link you can send someone.
  useEffect(() => {
    if (selected && selected.node_type_id !== selectedId) {
      const next = new URLSearchParams(params)
      next.set('object', selected.node_type_id)
      setParams(next, { replace: true })
    }
  }, [selected, selectedId, params, setParams])

  if (tables.isLoading) return <SkeletonRows rows={5} columns={4} label="Loading tables…" />
  if (tables.error) {
    return (
      <div className="brain-panel">
        <ErrorNotice error={tables.error} onRetry={tables.refetch} />
      </div>
    )
  }

  if (!tables.data?.length) {
    return (
      <div className="brain-panel">
        <EmptyState title="No tables yet">
          A table is created for each node type you add. Add one on the Nodes tab, or use
          Generate — the table appears with it, ready for records.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="brain-panel brain-panel-split">
      <div className="brain-rail" style={{ width: 220, minWidth: 220 }}>
        <h3>Nodes</h3>
        {tables.data.map((table) => {
          const category =
            model.node_types.find((n) => n.node_type_id === table.node_type_id)?.category ?? 'core'
          return (
            <button
              key={table.object_table_id}
              className="railitem"
              data-on={table.node_type_id === selected?.node_type_id}
              onClick={() => {
                const next = new URLSearchParams(params)
                next.set('object', table.node_type_id)
                setParams(next, { replace: true })
              }}
            >
              <CategoryDot category={category} />
              <span className="truncate">{table.node_type_key}</span>
              <span className="count">{table.row_count}</span>
            </button>
          )
        })}
      </div>

      {selected && <ObjectRowsView graphId={graphId} table={selected} />}
    </div>
  )
}
